import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {saveAs} from 'file-saver';
import {Subject, finalize, takeUntil} from 'rxjs';
import {fuseAnimations} from '@fuse/animations';
import {FuseAlertType} from '@fuse/components/alert';
import {FuseConfirmationService} from '@fuse/services/confirmation';
import {FuseConfirmationDialogComponent} from '@fuse/services/confirmation/dialog/dialog.component';
import {AccountRole} from '@enums/AccountRole';
import {User} from '@models/user.interface';
import {UserService} from '@core/user/user.service';
import {PolizaService} from '@services/poliza.service';
import {PolizaResponse} from '@models/poliza.interface';
import {PolizaStatus} from '@enums/PolizaStatus';
import {PolizaFormComponent} from '@provider/polizas/polizas/form/poliza-form.component';

/**
 * Tabla de pólizas de un proveedor específico, para uso de MASTER desde el perfil del proveedor
 * (AC-1). Permite subir una póliza en representación del proveedor (queda ACEPTADA_VIGENTE de
 * inmediato), editar cualquier campo sin importar el estatus (AC-4) y eliminar sin restricción de
 * estatus (AC-6) — reusa {@link PolizaFormComponent} en un modo distinto al flujo del proveedor.
 */
@Component({
    selector: 'app-polizas',
    templateUrl: './polizas.component.html',
    styleUrls: ['./polizas.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: false
})
export class PolizasComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() proveedorId: number;
    @Input() proveedorNombre: string;

    @ViewChild(MatPaginator) paginator: MatPaginator;

    dataSource: MatTableDataSource<PolizaResponse> = new MatTableDataSource<PolizaResponse>([]);
    displayedColumns: string[] = ['numeroPoliza', 'aseguradora', 'monto', 'vigenciaInicio', 'vigenciaFin', 'pagada', 'estatus', 'acciones'];
    readonly PolizaStatus = PolizaStatus;
    readonly accountRole: typeof AccountRole = AccountRole;
    user: User;

    alertEliminar: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertDescargar: { type: FuseAlertType; message: string } = {type: 'info', message: ''};

    private readonly _alertTime: number = 5000;
    private _showAlertEliminar: boolean = false;
    private _showAlertDescargar: boolean = false;
    private readonly _unsubscribeAll$: Subject<any> = new Subject<any>();

    constructor(
        private readonly _polizaService: PolizaService,
        private readonly _dialog: MatDialog,
        private readonly _fuseConfirmationService: FuseConfirmationService,
        private readonly _userService: UserService,
        private readonly _cdr: ChangeDetectorRef
    ) {
    }

    get showAlertEliminar(): boolean {
        return this._showAlertEliminar;
    }

    set showAlertEliminar(val: boolean) {
        this._showAlertEliminar = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertEliminar = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    get showAlertDescargar(): boolean {
        return this._showAlertDescargar;
    }

    set showAlertDescargar(val: boolean) {
        this._showAlertDescargar = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertDescargar = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    ngOnInit(): void {
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((user: User): void => {
            this.user = user;
            this._cdr.markForCheck();
        });
        this._cargarPolizas();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // El primer disparo de ngOnChanges ocurre antes de ngOnInit (orden estándar del
        // lifecycle de Angular), cuando `this.user` todavía no se resuelve desde user$ —
        // ngOnInit ya hace la carga inicial correcta una vez que el usuario está disponible.
        if (!changes['proveedorId']?.isFirstChange()) {
            this._cargarPolizas();
        }
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
    }

    ngOnDestroy(): void {
        this._unsubscribeAll$.next(null);
        this._unsubscribeAll$.complete();
    }

    descargarPdf(id: number, numeroPoliza: string): void {
        this.showAlertDescargar = false;
        this._polizaService.descargarPdf(id).pipe(
            finalize((): void => {
                this.showAlertDescargar = true;
            })
        ).subscribe({
            next: (blob: Blob): void => {
                saveAs(blob, `poliza_${numeroPoliza}.pdf`);
                this.alertDescargar = {type: 'success', message: 'PDF descargado correctamente.'};
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error al descargar PDF', err);
                this.alertDescargar = {type: 'error', message: 'Ocurrió un error al descargar el PDF.'};
            }
        });
    }

    abrirFormularioCarga(): void {
        const dialogRef = this._dialog.open(PolizaFormComponent, {
            width: '50%',
            panelClass: 'ghost-white-dialog',
            disableClose: true,
            data: {proveedorId: this.proveedorId, proveedorNombre: this.proveedorNombre}
        });
        dialogRef.afterClosed().subscribe((resultado: boolean): void => {
            if (resultado) {
                this._cargarPolizas();
            }
        });
    }

    editarPoliza(poliza: PolizaResponse): void {
        const dialogRef = this._dialog.open(PolizaFormComponent, {
            width: '50%',
            panelClass: 'ghost-white-dialog',
            disableClose: true,
            data: {modo: 'edit-master', poliza, proveedorNombre: this.proveedorNombre}
        });
        dialogRef.afterClosed().subscribe((resultado: boolean): void => {
            if (resultado) {
                this._cargarPolizas();
            }
        });
    }

    eliminarPoliza(poliza: PolizaResponse): void {
        const confirmation: MatDialogRef<FuseConfirmationDialogComponent> = this._fuseConfirmationService.open({
            title: 'Eliminar póliza',
            message: `¿Confirma la eliminación de la póliza <strong>${poliza.numeroPoliza}</strong>? Esta acción no se puede deshacer.`,
            icon: {name: 'heroicons_outline:trash', color: 'warn'},
            actions: {
                confirm: {label: 'Eliminar', color: 'warn'},
                cancel:  {label: 'Cancelar'}
            }
        });

        confirmation.afterClosed().subscribe((result: string): void => {
            if (result === 'confirmed') {
                this._polizaService.deletePoliza(poliza.id).pipe(
                    finalize((): void => {
                        this.showAlertEliminar = true;
                    })
                ).subscribe({
                    next: (): void => {
                        this.alertEliminar = {
                            type: 'success',
                            message: `La póliza ${poliza.numeroPoliza} se ha eliminado correctamente.`
                        };
                        this._cargarPolizas();
                    },
                    error: (err: HttpErrorResponse): void => {
                        console.error('Error al eliminar póliza', err);
                        this.alertEliminar = {type: 'error', message: 'Ocurrió un error al eliminar la póliza.'};
                    }
                });
            }
        });
    }

    private _cargarPolizas(): void {
        if (!this.proveedorId) {
            return;
        }
        const esCompania: boolean = this.user?.rol === this.accountRole.adminHotel
            || this.user?.rol === this.accountRole.accountant;
        const obs$ = esCompania
            ? this._polizaService.getPolizasByCompany({proveedorId: this.proveedorId})
            : this._polizaService.getAllPolizas({proveedorId: this.proveedorId});
        obs$.subscribe({
            next: (data: PolizaResponse[]): void => {
                this.dataSource.data = data;
                this._cdr.markForCheck();
            },
            error: (err: Error): void => console.error('Error al cargar pólizas', err)
        });
    }
}
